/**
 * DV-STAGE-003: Data model and input sources types
 */

export type StageInputSource = 'sample' | 'last_output' | 'custom';

export type StageId = 'script' | 'vision' | 'storyboard' | 'frames' | 'video' | 'qc' | 'export' | 'music';

export interface SampleItem {
  id: string;
  label: string;
  summary?: string;
  stage: string;
  input: any;
  createdAt?: string;
  tags?: string[];
  isDefault?: boolean;
}

export interface LastOutputRef {
  origin: 'execution' | 'session';
  executionId?: string;
  stage?: string;
  updatedAt?: string;
}

export interface StageOverrides {
  provider?: string;
  model?: string;
  params?: Record<string, any>;
}

export interface PromptPreview {
  text: string;
  variables: Record<string, any>;
  hash: string;
  tokensEstimate?: number;
  requestId?: string;
}

export interface EffectiveInputEnvelope {
  stage: string;
  source: StageInputSource;
  input: any;
  meta?: Record<string, any>;
}

export interface StageTesterState {
  source: StageInputSource;
  sampleId?: string;
  customJson?: string;
  effectiveInput?: any;
  lastOutputRef?: LastOutputRef;
  overrides?: StageOverrides;
  promptPreview?: PromptPreview;

  // UI state
  isLoadingSamples?: boolean;
  isLoadingLastOutput?: boolean;
  isValidatingCustom?: boolean;
  customJsonError?: string;
  samplesError?: string;
  lastOutputError?: string;
}

export interface SamplesCache {
  [stage: string]: {
    items: SampleItem[];
    fetchedAt: number;
    nextCursor?: string;
    requestId?: string;
  };
}

export interface SessionStorageState {
  source: StageInputSource;
  sampleId?: string;
  customJson?: string;
  overrides?: StageOverrides;
  lastOutputRef?: LastOutputRef;
}

export interface InputSourcePickerProps {
  stage: StageId;
  source: StageInputSource;
  onSourceChange: (source: StageInputSource) => void;
  samples: SampleItem[];
  onSampleSelect: (sampleId: string) => void;
  customJson: string;
  onCustomChange: (json: string) => void;
  lastOutputRef?: LastOutputRef;
  onLastOutputRefChange?: (ref: LastOutputRef) => void;
  isLoadingSamples?: boolean;
  samplesError?: string;
  customJsonError?: string;
  lastOutputError?: string;
}

export interface StageOutput {
  id: string;
  stage: string;
  executionId: string;
  iterationNumber: number;
  output: any;
  artifacts?: any[];
  createdAt: string;
  status: 'completed' | 'failed';
}

// Stage dependency mapping for last_output resolution
export const STAGE_DEPENDENCIES: Record<StageId, StageId | null> = {
  script: null, // First stage
  vision: 'script',
  storyboard: 'vision',
  frames: 'storyboard',
  video: 'frames',
  qc: 'video',
  export: 'qc',
  music: 'video' // Music can run parallel to QC
};

// Session storage keys
export const SESSION_STORAGE_KEYS = {
  stageTester: (stage: string) => `dv:stageTester:v1:${stage}`,
  sessionOutputs: 'dv:stageTester:sessionOutputs'
} as const;

// Cache settings
export const CACHE_SETTINGS = {
  samples: {
    ttlMs: 10 * 60 * 1000, // 10 minutes
    maxItems: 50
  },
  sessionOutputs: {
    maxEntries: 20 // Keep last 20 stage outputs per session
  }
} as const;